import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  TextField,
  MenuItem,
  Alert,
  Tooltip,
  LinearProgress,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  CloudUpload,
  Close,
  Refresh as RefreshIcon,
  Storage,
  TableChart,
  FileUpload,
} from '@mui/icons-material';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";

// API client
import apiClient from '../api/client';
import { fetchActualsThrough, postActualsThrough } from "../api/upload";

interface TableInfo {
  id: number;
  table_name: string;
  row_count: number;
}

const LeaveReviewPage: React.FC = () => {
  // Tab management
  const [activeTab, setActiveTab] = useState(0);

  // Tables state
  const [tables, setTables] = useState<TableInfo[]>([]);

  // Fetch tables from API on mount 
   useEffect(() => {
     fetchDatatables();
     fetchLeaves();

   }, []);


   const fetchLeaves = async () =>{
    try{
      const leaveResponse = await apiClient.get(`/api/v1/greythr/leave/years/2025/balance`)
      const tablesData = leaveResponse
      console.log("[leaveResponse]: ", leaveResponse)
    }catch(error){

    }

   };

    const fetchDatatables = async () => {
      try {
        const datatableResponse = await apiClient.get(`/api/v1/fileuploads/tables`);
        // Handle different response formats
        const tablesData = datatableResponse.data?.data || datatableResponse.data || [];
        setTables(Array.isArray(tablesData) ? tablesData : []);
      } catch (error) {
        setTables([]); // Ensure tables is always an array
        setSnackbar({
          open: true,
          message: "Failed to fetch datatables",
          severity: "error",
        });
      }
    };


  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  // Actuals Through (single date)
  const [actualsThroughDate, setActualsThroughDate] = useState<string>("");
  const [loadingActualsThrough, setLoadingActualsThrough] =
    useState<boolean>(false);
  const [savingActualsThrough, setSavingActualsThrough] =
    useState<boolean>(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingActualsThrough(true);
      try {
        const actual = await fetchActualsThrough();
        if (!mounted) return;
        setActualsThroughDate(actual ?? "");
      } catch {
        // non-blocking; field just remains empty
      } finally {
        if (mounted) setLoadingActualsThrough(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Dialog states
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [refreshDialogOpen, setRefreshDialogOpen] = useState(false);
  // const [tableDialogMode, setTableDialogMode] = useState<'create' | 'edit' | 'view'>('create');
  // Upload result modal state
  interface UploadResultTableInfo {
    total_rows: number;
    imported: number;
    skipped: number;
    errors: number;
    validation_errors?: string[];
    duplicate_errors?: string[];
    // Backward-compat (older API responses)
    error_details?: string[];
  }
  interface UploadResult {
    success: boolean;
    tables?: Record<string, UploadResultTableInfo>;
    sheets_found?: string[];
  }
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [resultErrorTab, setResultErrorTab] = useState<number>(0); // 0 = validation, 1 = duplicate

  type ParsedUploadError = {
    key: string;
    tableName: string;
    raw: string;
    rowNumber?: number;
    employeeId?: string;
    fieldName?: string;
    employeeName?: string;
    category: 'validation' | 'duplicate';
  };

  const parsedUploadErrors = React.useMemo(() => {
    const tables = uploadResult?.tables;
    if (!tables) {
      return {
        all: [] as ParsedUploadError[],
        validation: [] as ParsedUploadError[],
        duplicate: [] as ParsedUploadError[],
      };
    }

    const rowRe = /Row\s+(\d+)\s*:/i;
    const missingFieldRe = /Missing required field\s+'([^']+)'/i;
    const employeeIdRe = /Employee ID\s+'([^']+)'/i;
    const parenRe = /\(([^)]+)\)\s*$/;

    const validation: ParsedUploadError[] = [];
    const duplicate: ParsedUploadError[] = [];

    const pushList = (
      list: ParsedUploadError[],
      tableName: string,
      raw: string,
      idx: number,
      category: ParsedUploadError['category']
    ) => {
      const rowMatch = raw.match(rowRe);
      const rowNumber = rowMatch ? Number(rowMatch[1]) : undefined;

      const missingMatch = raw.match(missingFieldRe);
      const employeeIdMatch = raw.match(employeeIdRe);
      const employeeId = employeeIdMatch?.[1];

      const parenMatch = raw.match(parenRe);
      const employeeName = parenMatch?.[1];

      list.push({
        key: `${category}-${tableName}-${rowNumber ?? 'na'}-${employeeId ?? 'na'}-${idx}`,
        tableName,
        raw,
        rowNumber,
        employeeId,
        fieldName: missingMatch?.[1],
        employeeName,
        category,
      });
    };

    Object.entries(tables).forEach(([tableName, info]) => {
      const validationList = info?.validation_errors;
      if (Array.isArray(validationList) && validationList.length > 0) {
        validationList.forEach((raw, idx) => pushList(validation, tableName, raw, idx, 'validation'));
      }

      const duplicateList = info?.duplicate_errors;
      if (Array.isArray(duplicateList) && duplicateList.length > 0) {
        duplicateList.forEach((raw, idx) => pushList(duplicate, tableName, raw, idx, 'duplicate'));
      }

      // Backward-compat: if older API sent error_details, treat them as validation.
      const legacy = info?.error_details;
      if (Array.isArray(legacy) && legacy.length > 0) {
        legacy.forEach((raw, idx) => pushList(validation, tableName, raw, idx, 'validation'));
      }
    });

    const all = [...validation, ...duplicate];
    return { all, validation, duplicate };
  }, [uploadResult]);

  React.useEffect(() => {
    if (!resultDialogOpen) return;
    // Default to validation tab if available, otherwise duplicate.
    setResultErrorTab(parsedUploadErrors.validation.length > 0 ? 0 : 1);
  }, [parsedUploadErrors.validation.length, resultDialogOpen]);

  const ResultErrorRow = React.memo(function ResultErrorRow({ item }: { item: ParsedUploadError }) {
    return (
      <Box sx={{ px: 1.5, py: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ minWidth: 140 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
              {item.rowNumber ? `Row ${item.rowNumber}` : 'Row —'}
            </Typography>
            {/* <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {formatTableName(item.tableName)}
            </Typography> */}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontSize: 12.5, lineHeight: 1.45, color: 'text.primary' }}>
              {item.raw}
            </Typography>
          </Box>

          <Box sx={{ minWidth: 140, textAlign: 'right' }}>
            {item.employeeId ? (
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                  color: 'text.primary',
                  fontWeight: 600,
                }}
              >
                {item.employeeId}
              </Typography>
            ) : (
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                —
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  });


  // Upload dialog state
  const [selectedUploadTable, setSelectedUploadTable] = useState<string>('');
  const [selectedFinancialYear, setSelectedFinancialYear] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isTablePreselected, setIsTablePreselected] = useState(false);

  // Refresh dialog state
  const [refreshTableRowCount, setRefreshTableRowCount] = useState<number>(0);
  const [rowCountConfirm, setRowCountConfirm]= useState<string>('');
  
  const [selectedRefreshTable, setSelectedRefreshTable] = useState<string>('');
  const [refreshProgress, setRefreshProgress] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isRefreshTablePreselected, setIsRefreshTablePreselected] = useState(false);

  const isFinancialYearRequired = selectedUploadTable === 'pnl_statements_v2';

  const financialYearOptions = React.useMemo(() => {
    const currentYear = new Date().getFullYear();
    // Generate the 20 financial years BEFORE the current year (most recent first).
    // Example (if current year is 2026): FY2025-26, FY2024-25, ... (20 total)
    return Array.from({ length: 20 }, (_, i) => {
      const startYear = (currentYear - 1) - i;
      const endYearShort = String((startYear + 1) % 100).padStart(2, '0');
      return `FY${startYear}-${endYearShort}`;
    });
  }, []);

  // Alert state
  const [alert, setAlert] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Upload dialog handlers
  const openUploadDialog = (tableName?: string) => {
    setSelectedUploadTable(tableName || '');
    setIsTablePreselected(!!tableName); // Mark as preselected if tableName is provided
    setSelectedFinancialYear('');
    setSelectedFile(null);
    setUploadProgress(0);
    setIsUploading(false);
    setUploadDialogOpen(true);
  };

  const closeUploadDialog = () => {
    setUploadDialogOpen(false);
    setSelectedUploadTable('');
    setIsTablePreselected(false);
    setSelectedFinancialYear('');
    setSelectedFile(null);
    setUploadProgress(0);
    setIsUploading(false);
  };

  const closeResultDialog = () => {
    setResultDialogOpen(false);
    setUploadResult(null);
    setResultErrorTab(0);
  };

  // Refresh dialog handlers
  const openRefreshDialog = (table?:any) => {
    const tableName = table?.table_name;
    const rowCount = Number(table?.row_count ?? 0);
    setRefreshTableRowCount(rowCount || 0);
    setSelectedRefreshTable(tableName || '');
    setIsRefreshTablePreselected(!!tableName);
    setRefreshProgress(0);
    setIsRefreshing(false);
    setRefreshDialogOpen(true);
  };

  const closeRefreshDialog = () => {
    setRowCountConfirm('')
    setRefreshDialogOpen(false);
    setSelectedRefreshTable('');
    setIsRefreshTablePreselected(false);
    setRefreshProgress(0);
    setIsRefreshing(false);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['.xlsx', '.xls', '.csv'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      if (!validTypes.includes(fileExtension)) {
        setAlert({ open: true, message: 'Please select a valid Excel or CSV file', severity: 'error' });
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleRefreshTable = async () => {
    if (!selectedRefreshTable) {
      setSnackbar({ open: true, message: 'Please select a table', severity: 'error' });
      return;
    }
    if(!(Number(rowCountConfirm) === refreshTableRowCount)) return;

    setIsRefreshing(true);
    setRefreshProgress(0);

    // Simulate progress while refreshing (backend does not currently report progress)
    const progressInterval = setInterval(() => {
      setRefreshProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 200);

    try {
      await apiClient.delete(`/api/v1/auth/table/${selectedRefreshTable}`);
      // Refresh table list/counts after successful backend refresh
      await fetchDatatables();

      clearInterval(progressInterval);
      setRefreshProgress(100);
      setSnackbar({
        open: true,
        message: `${formatTableName(selectedRefreshTable)} was refreshed successfully.`,
        severity: 'success',
      });

      setTimeout(() => {
        closeRefreshDialog();
      }, 500);
    } catch (error: any) {
      clearInterval(progressInterval);
      const errorMessage = error?.response?.data?.detail ||
                          error?.response?.data?.message ||
                          error?.message ||
                          'Error refreshing table';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
      setIsRefreshing(false);
      setRefreshProgress(0);
    } 
  };


  const handleUpload = async () => {
    if (!selectedUploadTable || !selectedFile) {
      setSnackbar({ open: true, message: 'Please select a table and file', severity: 'error' });
      return;
    }
    if (isFinancialYearRequired && !selectedFinancialYear) {
      setSnackbar({ open: true, message: 'Please select a financial year', severity: 'error' });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (isFinancialYearRequired) {
        formData.append('financial_year', selectedFinancialYear);
      }

      // Simulate progress while uploading
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const response = await apiClient.post("/api/v1/fileuploads/upload-excel",formData,
        {
          params: { target_table: selectedUploadTable },
          headers: { "Content-Type": undefined, },
          timeout: 5 * 60 * 1000, // 5 minutes timeout for large files
        }
      );
      
      await fetchDatatables();

      clearInterval(progressInterval);
      setUploadProgress(100);

      // show detailed result modal using API response if available
      const result = response?.data ?? response;
      setUploadResult(result);
      setResultDialogOpen(true);
      setSnackbar({ 
        open: true, 
        message: `File "${selectedFile.name}" uploaded successfully to ${formatTableName(selectedUploadTable)}!`, 
        severity: 'success' 
      });
      
      // Brief delay to show 100% progress before closing
      setTimeout(() => {
        closeUploadDialog();
      }, 500);

    } catch (error: any) {
      console.log("Upload error details:", error);
      const errorMessage = error?.response?.data?.detail || 
                          error?.response?.data?.message || 
                          error?.message || 
                          'Error uploading file';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
      setIsUploading(false);
      setUploadProgress(0);
    }
  };


  // Helper to format table name for display
  const formatTableName = (name: string) => {
    return name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  function onSelectRefreshTable(tableName: any) {
    setSelectedRefreshTable(tableName);
    const tblRowCount =  tables.find((t) => t.table_name === tableName)?.row_count || 0;
    setRefreshTableRowCount(tblRowCount);
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Actual Through goes here */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2,
          border: 1,
          borderColor: "divider",
          display: "flex",
          gap: 2,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <Typography sx={{ fontWeight: 600, minWidth: 140 }}>
          Actuals Through
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Actuals Through"
            value={actualsThroughDate ? dayjs(actualsThroughDate) : null}
            onChange={async (newValue: Dayjs | null) => {
              const formatted = newValue ? newValue.format("YYYY-MM-DD") : "";
              setActualsThroughDate(formatted);
              if (!formatted) return;
              setSavingActualsThrough(true);
              try {
                await postActualsThrough(formatted);
                setSnackbar({
                  open: true,
                  message: "Actuals Through updated successfully.",
                  severity: "success",
                });
                window.dispatchEvent(
                  new CustomEvent("actualsThroughUpdated", { detail: formatted }),
                );
              } catch (error: any) {
                setSnackbar({
                  open: true,
                  message:
                    error?.response?.data?.detail ||
                    error?.message ||
                    "Failed to update Actuals Through",
                  severity: "error",
                });
              } finally {
                setSavingActualsThrough(false);
              }
            }}
            slotProps={{
              textField: {
                size: "small",
                sx: { minWidth: 260 },
                InputLabelProps: { shrink: true },
                disabled: loadingActualsThrough || savingActualsThrough,
              },
            }}
            format="DD/MM/YYYY"
          />
        </LocalizationProvider>
        {loadingActualsThrough || savingActualsThrough ? (
          <CircularProgress size={18} />
        ) : null}
      </Paper>
      
      {/* Page Header */}
      {/* <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <CloudUpload sx={{ fontSize: 32, color: '#1e40af' }} />
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a' }}>
            Upload Data
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ color: '#64748b' }}>
          Manage database tables and upload data from Excel files
        </Typography>
      </Box> */}

      {/* Alert */}
      {alert.open && (
        <Alert
          severity={alert.severity}
          onClose={() => setAlert({ ...alert, open: false })}
          sx={{ mb: 3 }}
        >
          {alert.message}
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="upload data tabs">
          <Tab icon={<TableChart sx={{ fontSize: 20 }} />} iconPosition="start" label="Leaves" />
          {/* <Tab icon={<History sx={{ fontSize: 20 }} />} iconPosition="start" label="Upload History" /> */}
        </Tabs>
      </Box>

      {/* Tab 1: Leave balance Tables */}
      {activeTab === 0 && (
        <Box>
          {/* Actions Bar */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
     
          </Box>

          {/* Summary Card */}
          <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f8fafc', display: 'inline-block' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e40af', mb: 0.5 }}>
              {tables.length}
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              Total Employees
            </Typography>
          </Paper>

          {/* Tables DataTable */}
          <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: 'hidden', border: 1, borderColor: 'divider', boxShadow: 1 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Si.No</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Table Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Row Count</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tables.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                      <Typography sx={{ color: '#64748b' }}>No tables found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  tables.map((table, index) => (
                    <TableRow key={table.id} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Storage sx={{ fontSize: 18, color: '#64748b' }} />
                          <Box>
                            {/* <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatTableName(table.table_name)}</Typography> */}
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>{table.table_name}</Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Row Count */}

                    <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box>
                            <Typography variant="caption" sx={{ color: '#94a3b8' }}>{table.row_count}</Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell align="center">
                      <Tooltip title="Upload Data">
                          <IconButton
                            size="small"
                            onClick={() => openUploadDialog(table.table_name)}
                            sx={{ color: '#059669' }}
                          >
                            <FileUpload fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Refresh table">
                          <IconButton
                            size="small"
                            onClick={() => openRefreshDialog(table)}
                            sx={{ color: '#b00f04', mr: 1 }}
                          >
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

    
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Tab 2: Upload History */}

      {/* Refresh Dialog */}
      <Dialog open={refreshDialogOpen} onClose={closeRefreshDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Refresh table
          </Typography>
          <IconButton onClick={closeRefreshDialog} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            
            {/* header */}
            {selectedRefreshTable && (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>
                  {Number(refreshTableRowCount) > 0
                    ? `You're about to delete ${refreshTableRowCount} records.`
                    : 'No records for deletion.'}
                </Typography>

                {Number(refreshTableRowCount) > 0 && (
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Deleted records can't be restored. Records created after this submission will not be deleted.
                  </Typography>
                )}
              </Box>
            )}

            <FormControl fullWidth required>
              <InputLabel>Select Table</InputLabel>
              <Select
                value={selectedRefreshTable}
                label="Select Table"
                onChange={(e) => onSelectRefreshTable(e.target.value)}
                inputProps={{ readOnly: isRefreshTablePreselected }}
                sx={isRefreshTablePreselected ? {
                  backgroundColor: '#f1f5f9',
                  '& .MuiSelect-icon': { display: 'none' },
                } : {}}
              >
                {tables.map((table) => (
                  <MenuItem key={table.id} value={table.table_name}>
                    {formatTableName(table.table_name)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>


           {selectedRefreshTable && (Number(refreshTableRowCount) > 0) && (
            <Box>
              {/* <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Type the number of records below to delete
              </Typography> */}
              <InputLabel sx={{ fontWeight: 480, fontSize: '0.750rem', mb: 1 }}>Type the number of records below to delete</InputLabel>
              <TextField
                value={rowCountConfirm}
                  onChange={(e) => {
                      const next = e.target.value.replace(/\D/g, '');
                      setRowCountConfirm(next);
                    }}
                placeholder={String(refreshTableRowCount)}
                fullWidth
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              />
            </Box>)}



            {isRefreshing && (
              <Box sx={{ width: '100%' }}>
                <LinearProgress variant="determinate" value={refreshProgress} sx={{ height: 8, borderRadius: 4 }} />
                <Typography variant="body2" sx={{ color: '#64748b', mt: 1, textAlign: 'center' }}>
                  Refreshing... {refreshProgress}%
                </Typography>
              </Box>
            )}

            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="body2" component="div">
                This action triggers a backend refresh for the selected table.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeRefreshDialog} variant="outlined" disabled={isRefreshing}>
            Cancel
          </Button>
          <Button
                onClick={handleRefreshTable}
                variant="contained"
                disabled={
                  isRefreshing ||
                  !selectedRefreshTable ||
                  rowCountConfirm === '' ||
                  Number(rowCountConfirm) !== refreshTableRowCount
                }
                startIcon={isRefreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                sx={{ backgroundColor: '#1e40af' }}>
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </Button>
        </DialogActions>
      </Dialog>


      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={closeUploadDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Upload Excel Data
          </Typography>
          <IconButton onClick={closeUploadDialog} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            {/* Table Selection */}
            <FormControl fullWidth>
              <InputLabel>Select Table *</InputLabel>
              <Select
                value={selectedUploadTable}
                label="Select Table *"
                onChange={(e) => {
                  const nextTable = e.target.value;
                  setSelectedUploadTable(nextTable);
                  if (nextTable !== 'pnl_statements_v2') {
                    setSelectedFinancialYear('');
                  }
                }}
                inputProps={{ readOnly: isTablePreselected }}
                sx={isTablePreselected ? { 
                  backgroundColor: '#f1f5f9',
                  '& .MuiSelect-icon': { display: 'none' }
                } : {}}
              >
                {tables.map((table) => (
                  <MenuItem key={table.id} value={table.table_name}>
                    {formatTableName(table.table_name)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {isFinancialYearRequired && (
              <FormControl fullWidth required>
                <InputLabel>Financial year</InputLabel>
                <Select
                  value={selectedFinancialYear}
                  label="Financial year"
                  onChange={(e) => setSelectedFinancialYear(e.target.value)}
                >
                  {financialYearOptions.map((fy) => (
                    <MenuItem key={fy} value={fy}>
                      {fy}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* File Upload Area */}
            <Box
              sx={{
                border: '2px dashed #e5e7eb',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                backgroundColor: '#f8fafc',
                cursor: 'pointer',
                '&:hover': { borderColor: '#1e40af', backgroundColor: '#eff6ff' },
              }}
              onClick={() => document.getElementById('file-upload')?.click()}
            >
              <input
                type="file"
                id="file-upload"
                accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              <CloudUpload sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                {selectedFile ? selectedFile.name : 'Click to select or drag and drop'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Supported format: .xlsx
              </Typography>
            </Box>

            {/* Upload Progress */}
            {isUploading && (
              <Box sx={{ width: '100%' }}>
                <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 8, borderRadius: 4 }} />
                <Typography variant="body2" sx={{ color: '#64748b', mt: 1, textAlign: 'center' }}>
                  Uploading... {uploadProgress}%
                </Typography>
              </Box>
            )}

            {/* Instructions */}
            <Alert severity="info" sx={{ mt: 1 }}>
              {/* Render as a div to avoid <ul> inside a <p> (Typography defaults to <p>) */}
              <Typography variant="body2" component="div">
                <strong>Instructions:</strong>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                  <li>Ensure your Excel file has headers in the first row</li>
                  <li>Column names must match the table schema</li>
                  {/* <li>Duplicate records will be updated, new records will be added</li> */}
                </ul>
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeUploadDialog} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={
              !selectedUploadTable ||
              !selectedFile ||
              isUploading ||
              (isFinancialYearRequired && !selectedFinancialYear)
            }
            startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : <FileUpload />}
            sx={{ backgroundColor: '#1e40af' }}
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Result Dialog */}
      <Dialog open={resultDialogOpen} onClose={closeResultDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Upload Results
          </Typography>
          <IconButton onClick={closeResultDialog} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {uploadResult?.success ? 'Upload succeeded' : 'Upload completed'}
            </Typography>

            {uploadResult?.tables ? (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', border: 1, borderColor: 'divider', boxShadow: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Table</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Total</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Imported</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Skipped</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Errors</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(uploadResult.tables).map(([tableName, info]) => (
                      <TableRow key={tableName}>
                        <TableCell>{formatTableName(tableName)}</TableCell>
                        <TableCell align="left">{info.total_rows}</TableCell>
                        <TableCell align="left">{info.imported}</TableCell>
                        <TableCell align="left">{info.skipped}</TableCell>
                        <TableCell align="left">{info.errors}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2">No detailed table results returned by the API.</Typography>
            )}

            {uploadResult?.sheets_found && (
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Sheets found</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {uploadResult.sheets_found.map((s) => (
                    <Chip key={s} label={s} size="small" />
                  ))}
                </Box>
              </Box>
            )}

            {parsedUploadErrors.all.length > 0 && (
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Errors Received
                </Typography>

                <Tabs
                  value={resultErrorTab}
                  onChange={(_e, v) => setResultErrorTab(v)}
                  variant="scrollable"
                  allowScrollButtonsMobile
                  sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
                >
                  <Tab label={`Validation errors`} />
                  <Tab label={`Duplicate errors`} />
                </Tabs>

                <Box
                  sx={{
                    maxHeight: 450,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                  }}
                >
                  {(() => {
                    const isValidation = resultErrorTab === 0;
                    const title = isValidation ? 'Validation errors' : 'Duplicate errors';
                    const items = isValidation ? parsedUploadErrors.validation : parsedUploadErrors.duplicate;
                    const total = items.length;

                    return (
                      <Box>
                        <Box sx={{ px: 1.5, py: 1.25, bgcolor: 'grey.50' }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
                            {title} ({total})
                          </Typography>
                        </Box>
                        <Divider />

                        {items.length === 0 ? (
                          <Box sx={{ px: 2, py: 2 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              No errors in this category.
                            </Typography>
                          </Box>
                        ) : (
                          <Box>
                            {items.map((item, idx) => (
                              <React.Fragment key={item.key}>
                                <ResultErrorRow item={item} />
                                {idx < items.length - 1 ? <Divider /> : null}
                              </React.Fragment>
                            ))}
                          </Box>
                        )}
                      </Box>
                    );
                  })()}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeResultDialog} variant="contained" sx={{ backgroundColor: '#1e40af' }}>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LeaveReviewPage;
